import useSWR from "swr";
import dayjs from "dayjs";

import {
  getPriceRows,
  getPriceTrend,
  getProducts,
  getProvinceOpts,
  getRetailers,
  getRetailerAnalyticsProductSnapshot,
  getRetailerAnalyticsDistinctProductCountByProvince,
} from "../../../api/priceReports";

const fmt = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "");

const keyPart = (filters = {}) =>
  JSON.stringify({
    retailers: filters?.retailers || [],
    products: filters?.products || [],
    provinces: filters?.provinces || [],
  });

export const DEFAULT_TREND_FILTER_MODE = "reportScoped";

const kTrend = (
  report,
  range,
  granularity,
  filters,
  trendFilterMode = DEFAULT_TREND_FILTER_MODE
) => [
  "price-directus:trend",
  trendFilterMode,
  report,
  fmt(range?.startDate),
  fmt(range?.endDate),
  granularity,
  keyPart(filters),
];

const kRows = (report, range, filters, showTbl) => {
  if (!showTbl) return null;

  return [
    "price-directus:rows",
    report,
    fmt(range?.startDate),
    fmt(range?.endDate),
    keyPart(filters),
  ];
};

const usePriceTrendData = ({
  report,
  range,
  granularity,
  filters,
  trendFilterMode = DEFAULT_TREND_FILTER_MODE,
}) => {
  return useSWR(
    kTrend(report, range, granularity, filters, trendFilterMode),
    () =>
      getPriceTrend(report, {
        range: {
          startDate: fmt(range?.startDate),
          endDate: fmt(range?.endDate),
        },
        granularity,
        filters,
        trendFilterMode,
      }),
    {
      revalidateOnFocus: false,
    }
  );
};

const usePriceRowsData = ({ report, range, filters, showTbl }) => {
  return useSWR(
    kRows(report, range, filters, showTbl),
    () =>
      getPriceRows(report, {
        range: {
          startDate: fmt(range?.startDate),
          endDate: fmt(range?.endDate),
        },
        filters,
      }),
    {
      revalidateOnFocus: false,
    }
  );
};

const useRetailerOptions = (enabled) => {
  return useSWR(
    enabled ? ["price-directus:filter:retailers"] : null,
    () => getRetailers(),
    {
      revalidateOnFocus: false,
    }
  );
};

const useProductOptions = (enabled) => {
  return useSWR(
    enabled ? ["price-directus:filter:products"] : null,
    () => getProducts(),
    {
      revalidateOnFocus: false,
    }
  );
};

const useProvinceOptions = (enabled) => {
  return useSWR(
    enabled ? ["price-directus:filter:provinces"] : null,
    () => getProvinceOpts(),
    {
      revalidateOnFocus: false,
    }
  );
};

/** Skip Retailer Analytics fetches when dates are missing or start is after end (invalid slice). */
const retailerAnalyticsSnapshotRangeAllowsFetch = (range) => {
  const start = range?.startDate;
  const end = range?.endDate;
  if (start == null || end == null) return false;
  if (!dayjs(start).isValid() || !dayjs(end).isValid()) return false;
  return !dayjs(start).startOf("day").isAfter(dayjs(end).endOf("day"));
};

const kRetailerAnalyticsProducts = (range, retailerName) => {
  if (!retailerName || !retailerAnalyticsSnapshotRangeAllowsFetch(range)) return null;
  return [
    "price-directus:retailer-analytics:products",
    fmt(range?.startDate),
    fmt(range?.endDate),
    retailerName,
  ];
};

const kRetailerAnalyticsProvinceCounts = (range, retailerName) => {
  if (!retailerName || !retailerAnalyticsSnapshotRangeAllowsFetch(range)) return null;
  return [
    "price-directus:retailer-analytics:province-distinct-products",
    fmt(range?.startDate),
    fmt(range?.endDate),
    retailerName,
  ];
};

const useRetailerAnalyticsProductSnapshot = ({ enabled, range, retailerName }) => {
  return useSWR(
    enabled ? kRetailerAnalyticsProducts(range, retailerName) : null,
    () =>
      getRetailerAnalyticsProductSnapshot({
        range: {
          startDate: fmt(range?.startDate),
          endDate: fmt(range?.endDate),
        },
        retailerName,
      }),
    { revalidateOnFocus: false }
  );
};

const useRetailerAnalyticsDistinctProductCountByProvince = ({ enabled, range, retailerName }) => {
  return useSWR(
    enabled ? kRetailerAnalyticsProvinceCounts(range, retailerName) : null,
    () =>
      getRetailerAnalyticsDistinctProductCountByProvince({
        range: {
          startDate: fmt(range?.startDate),
          endDate: fmt(range?.endDate),
        },
        retailerName,
      }),
    { revalidateOnFocus: false }
  );
};

export {
  usePriceTrendData,
  usePriceRowsData,
  useRetailerOptions,
  useProductOptions,
  useProvinceOptions,
  useRetailerAnalyticsProductSnapshot,
  useRetailerAnalyticsDistinctProductCountByProvince,
};
